import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  getAdditionalUserInfo,
  GoogleAuthProvider,
  sendEmailVerification,
  fetchSignInMethodsForEmail,
  signOut,
  updateProfile,
  confirmPasswordReset as firebaseConfirmPasswordReset,
  User as FirebaseUser,
  onAuthStateChanged,
  NextOrObserver,
  ErrorFn,
  CompleteFn,
  Auth,
} from 'firebase/auth';
import { isExpired, decodeToken } from 'react-jwt';
import { auth } from '@/shared/utils/firebase';
import { FirebaseIdentity, User } from '@/entities/user/types';
import { mapFirebaseUserToUser } from '@/entities/user/model';
import apiClient, { ApiError } from '@/shared/utils/axios';
import type {
  LoginCredentials,
  RegisterCredentials,
  AuthResult,
  InviteTokenPayload,
  RegistrationCleanup,
} from '@/entities/auth/types';
import type { ApiResponse } from '@/entities/api/types';
import { DOOH_ROLES } from '@/entities/user/types';
import {
  ACCEPTANCE_UNCERTAIN_MESSAGE,
  ACCEPTED_NOT_LOADED_MESSAGE,
  CREATE_UNCERTAIN_MESSAGE,
} from './constants';

class RegistrationError extends Error {
  constructor(
    message: string,
    readonly cleanup: RegistrationCleanup
  ) {
    super(message);
    this.name = 'RegistrationError';
  }
}

class AuthAPI {
  private googleProvider: GoogleAuthProvider;

  constructor() {
    this.googleProvider = new GoogleAuthProvider();
    this.googleProvider.setCustomParameters({
      prompt: 'select_account',
    });
  }

  private getAuth(): Auth {
    if (!auth) {
      throw new Error(
        'Firebase Auth is not initialized. Please check your Firebase configuration.'
      );
    }
    return auth;
  }

  private async getTokenFromUser(firebaseUser: FirebaseUser): Promise<string> {
    return firebaseUser.getIdToken();
  }

  private isDOOHUser(role: string): boolean {
    return (DOOH_ROLES as readonly string[]).includes(role);
  }

  private async loadDoohUser(): Promise<User> {
    const { data: user, message } =
      await apiClient.get<User>('/dooh_api/user/v1');

    if (!user) {
      throw new Error(message || 'Failed to load your DOOH account.');
    }

    if (!user.partner_id) {
      throw new Error(
        "Your invite hasn't been accepted yet. Please open the invite link from your email."
      );
    }

    if (!this.isDOOHUser(user.role)) {
      throw new Error(
        "This account doesn't have access to the DOOH platform. Ask your administrator for an invite."
      );
    }

    return user;
  }

  private async findDoohUser(): Promise<User | null> {
    try {
      const { data: user, message } =
        await apiClient.get<User>('/dooh_api/user/v1');

      if (!user) {
        throw new Error(message || 'Failed to load your DOOH account.');
      }

      return user;
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        return null;
      }

      throw error;
    }
  }

  private async provisionAndAcceptInvite(
    name: string,
    signature: string
  ): Promise<User> {
    try {
      await apiClient.post('/dooh_api/user/v1/create', { name });
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.status >= 400 &&
        error.status < 500
      ) {
        throw new RegistrationError(
          error.message || 'Failed to create your DOOH account.',
          'DELETE_FIREBASE_USER'
        );
      }

      const reconciledUser = await this.findDoohUser().catch(() => null);
      if (!reconciledUser) {
        throw new RegistrationError(CREATE_UNCERTAIN_MESSAGE, 'PRESERVE');
      }
    }

    try {
      await apiClient.post('/dooh_api/user/v1/accept-invite', { signature });
    } catch (error) {
      const alreadyAccepted =
        error instanceof ApiError &&
        error.status === 409 &&
        /accepted/i.test(error.message);

      if (
        error instanceof ApiError &&
        error.status >= 400 &&
        error.status < 500 &&
        !alreadyAccepted
      ) {
        throw new RegistrationError(
          error.message ||
            'This invite could not be accepted. Please request a new invite.',
          'DELETE_DOOH_USER'
        );
      }

      try {
        return await this.loadDoohUser();
      } catch {
        throw new RegistrationError(
          alreadyAccepted
            ? ACCEPTED_NOT_LOADED_MESSAGE
            : ACCEPTANCE_UNCERTAIN_MESSAGE,
          'PRESERVE'
        );
      }
    }

    try {
      return await this.loadDoohUser();
    } catch {
      throw new RegistrationError(ACCEPTED_NOT_LOADED_MESSAGE, 'PRESERVE');
    }
  }

  private async cleanupRegistration(
    firebaseUser: FirebaseUser,
    ownsFirebaseAccount: boolean,
    error: unknown
  ): Promise<void> {
    const cleanup =
      error instanceof RegistrationError
        ? error.cleanup
        : 'DELETE_FIREBASE_USER';

    if (ownsFirebaseAccount && cleanup === 'DELETE_FIREBASE_USER') {
      await firebaseUser.delete().catch(() => undefined);
    } else if (ownsFirebaseAccount && cleanup === 'DELETE_DOOH_USER') {
      await apiClient.delete('/dooh_api/user/v1/me').catch(() => undefined);
    }

    await signOut(this.getAuth()).catch(() => undefined);
  }

  async signInWithEmail(credentials: LoginCredentials): Promise<AuthResult> {
    const authInstance = this.getAuth();

    const result = await signInWithEmailAndPassword(
      authInstance,
      credentials.email,
      credentials.password
    );

    const user = mapFirebaseUserToUser(result.user);
    const token = await this.getTokenFromUser(result.user);

    try {
      const doohUser = await this.loadDoohUser();
      const mergedUser = { ...user, ...doohUser };
      this.saveUserToLocalStorage(mergedUser);
      return { user: mergedUser, token };
    } catch (error) {
      await signOut(authInstance).catch(() => undefined);
      throw error;
    }
  }

  async signUpWithEmail(
    credentials: RegisterCredentials,
    signature?: string
  ): Promise<AuthResult> {
    if (!signature) {
      throw new Error(
        'An invite is required to register. Please use the invite link sent to your email.'
      );
    }

    const decodedToken = decodeToken(signature) as InviteTokenPayload | null;
    if (!decodedToken) {
      throw new Error('Invalid invite link. Please request a new invite.');
    }
    if (isExpired(signature)) {
      throw new Error(
        'This invite link has expired. Please request a new invite.'
      );
    }
    if (
      decodedToken.email?.toLowerCase() !== credentials.email?.toLowerCase()
    ) {
      throw new Error(
        'This invite was sent to a different email address. Please sign up using the email that received the invite.'
      );
    }

    const authInstance = this.getAuth();

    const existingMethods = await fetchSignInMethodsForEmail(
      authInstance,
      credentials.email
    );
    if (existingMethods.includes('google.com')) {
      throw new Error(
        'An account already exists with this email via Google sign-in. Please sign in with Google instead.'
      );
    }
    if (existingMethods.includes('password')) {
      throw new Error(
        'An account already exists with this email and password. Please sign in instead.'
      );
    }
    if (existingMethods.length > 0) {
      throw new Error(
        'An account with this email already exists. Please sign in instead.'
      );
    }

    const result = await createUserWithEmailAndPassword(
      authInstance,
      credentials.email,
      credentials.password
    );

    try {
      const displayName =
        `${credentials.firstName} ${credentials.lastName}`.trim();
      await updateProfile(result.user, { displayName });
      await this.sendEmailVerification();

      const user = mapFirebaseUserToUser(result.user);
      const token = await this.getTokenFromUser(result.user);
      const doohUser = await this.provisionAndAcceptInvite(
        displayName,
        signature
      );
      const mergedUser = { ...user, ...doohUser };

      this.saveUserToLocalStorage(mergedUser);
      return { user: mergedUser, token };
    } catch (error) {
      await this.cleanupRegistration(result.user, true, error);
      throw error;
    }
  }

  async signInWithGoogle(): Promise<AuthResult> {
    const authInstance = this.getAuth();

    const result = await signInWithPopup(authInstance, this.googleProvider);
    const isNewAccount = getAdditionalUserInfo(result)?.isNewUser === true;

    if (isNewAccount) {
      await result.user.delete().catch(() => undefined);
      await signOut(authInstance).catch(() => undefined);
      throw new Error(
        'No DOOH account found. Please use your invite link to register.'
      );
    }

    const user = mapFirebaseUserToUser(result.user);
    const token = await this.getTokenFromUser(result.user);

    try {
      const doohUser = await this.loadDoohUser();
      const mergedUser = { ...user, ...doohUser };
      this.saveUserToLocalStorage(mergedUser);
      return { user: mergedUser, token };
    } catch (error) {
      await signOut(authInstance).catch(() => undefined);
      throw error;
    }
  }

  async registerWithGoogle(signature: string): Promise<AuthResult> {
    const decodedToken = decodeToken(signature) as InviteTokenPayload | null;
    if (!decodedToken) {
      throw new Error('Invalid invite link. Please request a new invite.');
    }
    if (isExpired(signature)) {
      throw new Error(
        'This invite link has expired. Please request a new invite.'
      );
    }

    const authInstance = this.getAuth();

    let result;
    try {
      result = await signInWithPopup(authInstance, this.googleProvider);
    } catch (error: unknown) {
      if (
        typeof error === 'object' &&
        error !== null &&
        'code' in error &&
        error.code === 'auth/account-exists-with-different-credential'
      ) {
        throw new Error(
          'This email is registered with email and password. Please sign in with email and password instead.'
        );
      }
      throw error;
    }

    const isNewAccount = getAdditionalUserInfo(result)?.isNewUser === true;

    try {
      const user = mapFirebaseUserToUser(result.user);
      const token = await this.getTokenFromUser(result.user);

      if (decodedToken.email?.toLowerCase() !== user.email?.toLowerCase()) {
        throw new RegistrationError(
          'This invite was sent to a different email address. Please register using the Google account that received the invite.',
          'DELETE_FIREBASE_USER'
        );
      }

      if (!isNewAccount) {
        const existingDoc = await this.findDoohUser();

        if (existingDoc?.partner_id && this.isDOOHUser(existingDoc.role)) {
          throw new RegistrationError(
            'An account already exists for this email. Please sign in via the login page.',
            'DELETE_FIREBASE_USER'
          );
        }
      }

      const doohUser = await this.provisionAndAcceptInvite(
        user.name,
        signature
      );
      const mergedUser = { ...user, ...doohUser };

      this.saveUserToLocalStorage(mergedUser);
      return { user: mergedUser, token };
    } catch (error) {
      await this.cleanupRegistration(result.user, isNewAccount, error);
      throw error;
    }
  }

  async sendEmailVerification(): Promise<void> {
    const authInstance = this.getAuth();

    if (!authInstance.currentUser) {
      throw new Error('No authenticated user found');
    }

    if (authInstance.currentUser.emailVerified) {
      console.warn('Email already verified');
      return;
    }

    await sendEmailVerification(authInstance.currentUser);
  }

  async updateUserProfile(userData: Partial<User>): Promise<User> {
    try {
      const updateData = {
        id: userData.id,
        updatedValues: userData,
      };

      const response = await apiClient.post<ApiResponse<User>>(
        '/sdk_api/v1/update-user',
        updateData
      );

      if (response.data.status === 'success') {
        const updatedUser = response.data.data;
        this.saveUserToLocalStorage(updatedUser);
        return updatedUser;
      } else {
        throw new Error(response.data.msg || 'Failed to update user');
      }
    } catch (error: any) {
      throw new Error(error?.message || 'Failed to update user profile');
    }
  }

  async updateUserImage(imageUrl: string, userId: string): Promise<void> {
    try {
      const updateData = {
        id: userId,
        updatedValues: { image: imageUrl },
      };
      await apiClient.post('/sdk_api/v1/update-user', updateData);
    } catch (error: any) {
      throw new Error(error?.message || 'Failed to update user image');
    }
  }

  private saveUserToLocalStorage(userData: User): void {
    try {
      localStorage.setItem('user', JSON.stringify(userData));
    } catch (error) {
      console.warn('Failed to save user data to localStorage:', error);
    }
  }

  getUserFromLocalStorage(): User | null {
    try {
      const userData = localStorage.getItem('user');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.warn('Failed to get user data from localStorage:', error);
      return null;
    }
  }

  clearUserFromLocalStorage(): void {
    try {
      localStorage.removeItem('user');
    } catch (error) {
      console.warn('Failed to clear user data from localStorage:', error);
    }
  }

  async sendPasswordReset(email: string): Promise<void> {
    await apiClient.post('/dooh_api/user/v1/forgot-password', { email });
  }

  async confirmPasswordReset(
    oobCode: string,
    newPassword: string
  ): Promise<void> {
    const authInstance = this.getAuth();
    await firebaseConfirmPasswordReset(authInstance, oobCode, newPassword);
  }

  async signOut(): Promise<void> {
    const authInstance = this.getAuth();
    this.clearUserFromLocalStorage();
    await signOut(authInstance);
  }

  async getCurrentUser(): Promise<FirebaseIdentity | null> {
    try {
      const authInstance = this.getAuth();
      if (!authInstance.currentUser) return null;
      return mapFirebaseUserToUser(authInstance.currentUser);
    } catch {
      return null;
    }
  }

  async getCurrentToken(): Promise<string | null> {
    try {
      const authInstance = this.getAuth();
      if (!authInstance.currentUser) return null;
      return await authInstance.currentUser.getIdToken();
    } catch (error) {
      console.error('Failed to get current token:', error);
      return null;
    }
  }

  onAuthStateChanged(
    nextOrObserver: NextOrObserver<FirebaseUser>,
    error?: ErrorFn,
    completed?: CompleteFn
  ) {
    try {
      const authInstance = this.getAuth();
      return onAuthStateChanged(authInstance, nextOrObserver, error, completed);
    } catch {
      console.warn('Firebase Auth is not initialized');
      return () => {};
    }
  }
}

export const authAPI = new AuthAPI();
export default authAPI;
